<template>
    <div class="highlighter display-flex align-items-center py-xs">
        <div
            class="highlighter__new-tab px-sm"
            v-tooltip="{ content: 'Open company in a new tab' }"
        >
            <a v-if="props.companyPk" :href="entityRouteLink(props.companyPk)" target="_blank">
                <font-awesome-icon class="text-dark" icon="fas fa-arrow-up-right-from-square" />
            </a>
        </div>
        <div
            class="highlighter__company display-flex align-items-center w-100 justify-between"
            :class="[{ 'highlighter__company--alt': props.watchlistPk }]"
            @click.prevent="gotoLink(props.watchlistPk)"
        >
            <div class="highlighter__content display-flex align-items-center w-100">
                <div class="highlighter__aligner link">
                    <span
                        v-for="(letter, index) in parsedTargetWords"
                        :key="`word_${index}`"
                        class="align-items-center"
                        :class="getClass(index % 2)"
                    >
                        {{ letter }}
                    </span>
                </div>
                <div class="highlighter__aligner">
                    <div
                        v-if="ticker"
                        class="highlighter__ticker display-inline-flex align-items-center ml-sm px-xs py-xxs bg-neutral-light"
                    >
                        <span
                            v-for="(letter, index) in parsedTargetTicker"
                            :key="`ticker_${index}`"
                            :class="getClass(index % 2)"
                        >
                            {{ letter }}
                        </span>
                    </div>
                </div>
                <div
                    v-if="props.watchlistPk && props.companyParams.address"
                    v-tooltip="{ content: props.companyParams.address, distance: 10 }"
                    class="px-sm display-flex align-items-center"
                >
                    <p class="text-sm mr-sm">|</p>
                    <p class="w-half overflow-ellipsis text-sm text-dark" style="width: 250px">
                        <span class="font-bold">Address:</span>
                        {{ props.companyParams.address.replace(/,/g, ', ') }}
                    </p>
                </div>
                <div
                    v-if="props.watchlistPk && props.companyParams.patent_count_us"
                    class="px-sm display-flex align-items-center"
                >
                    <p class="text-sm mr-lg">|</p>
                    <p class="w-half text-sm text-dark" style="white-space: nowrap">
                        <span class="font-bold">Patent Count:</span>
                        {{ parseInt(props.companyParams.patent_count_us).toLocaleString() }}
                    </p>
                </div>
            </div>
            <div
                v-if="props.watchlistPk"
                v-tooltip="{ content: 'Add company to current Watchlist', distance: 10 }"
                class="px-sm"
                @click.stop="addToWatchlist()"
            >
                <AonSpinner v-if="loadingAdd" :scale="0.2" />
                <font-awesome-icon
                    v-else
                    class="highlighter__icon text-dark"
                    icon="fas fa-circle-plus"
                />
            </div>
            <div v-if="props.country" class="px-sm display-flex align-items-center">
                <span class="country-chip" :class="countryFlag"></span>
            </div>
        </div>
        <CompanyPopover
            :show-popover="showPopover"
            :passed-company-details="props.companyParams"
            :popper-options="{ placement: 'right' }"
            @close-popover="showPopover = false"
        />
    </div>
</template>

<script setup>
import { computed, ref, inject, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { addToDataset } from '../../api/datasets'
import { useAuth } from '@/auth/authPlugin'
import { entityRouteLink } from '../../router/utils'
import CompanyPopover from '../helpers/CompanyPopover.vue'

const router = useRouter()
const emit = defineEmits(['clear'])
const logger = inject('logger')
const eventBus = inject('eventBus')

const props = defineProps({
    targetWords: {
        type: String,
        default: null,
    },
    searchTerm: {
        type: String,
        default: null,
    },
    effect: {
        type: String,
        default: 'highlight',
    },
    // May rethink this in the future - BB
    ticker: {
        type: String,
        default: null,
    },
    companyPk: {
        type: String,
        default: '',
    },
    watchlistPk: {
        type: String,
        default: null,
    },
    companyParams: {
        type: Object,
        default: () => ({}),
    },
    country: {
        type: String,
        default: null,
    },
    patentId: {
        type: String,
        default: null,
    },
})
const showPopover = ref(false)
const loadingAdd = ref(false)

const parsedSearchTerm = computed(() => {
    return '(' + props.searchTerm.trim().replace(/ +/g, '|') + ')'
})

const parsedTargetWords = computed(() => {
    return props.targetWords.split(new RegExp(parsedSearchTerm.value, 'gi'))
})

const parsedTargetTicker = computed(() => {
    return props.ticker.split(new RegExp(parsedSearchTerm.value, 'gi'))
})

const countryFlag = computed(() => {
    if (!props.country) {
        return ''
    }
    // `country-flag-icons` expects the country code to be uppercase ISO-3166-1 alpha-2.
    return `flag:${props.country.toUpperCase()}`
})

onBeforeUnmount(() => {
    eventBus.off('update-watchlist')
    eventBus.off('watchlistChanged')
})

const getClass = (i) => {
    let myClass = {}
    myClass[props.effect] = !!i
    return myClass
}

const gotoLink = (isWatchlist = false) => {
    if (!isWatchlist) {
        if (props.patentId) {
            emit('clear')
            router.push({
                name: 'Research Patent',
                params: { patentId: props.patentId },
            })
        } else {
            emit('clear')
            router.push({
                name: 'Research Company',
                params: { targetPk: props.companyPk },
                query: { tabName: 'profile' },
            })
        }
        return
    }

    if (props.watchlistPk) {
        showPopover.value = true
    }
}

const addToWatchlist = async () => {
    loadingAdd.value = true
    try {
        const { user } = useAuth()
        let data = await addToDataset(props.watchlistPk, user.value.id, 'Entity', [
            { id_value: props.companyParams.aon_entity_pk },
        ])
    } catch (err) {
        logger.error(err)
        throw err
    } finally {
        eventBus.emit('update-watchlist')
        eventBus.emit('watchlistChanged', 'Entity')
        loadingAdd.value = false
    }
}
</script>

<style lang="scss">
.highlighter {
    &__company {
        min-height: 30px;
    }

    &__content {
        /* Remove white-space: nowrap; to allow truncation */
    }

    &__aligner {
        /* Remove white-space: nowrap; to allow truncation */
        overflow: hidden;
    }

    &__ticker {
        border-radius: var(--border-radius-sm);
        background-color: var(--stone-50);
        transition: all ease-in-out 0.3s;

        span {
            color: var(--text-stone-800);
            transition: all ease-in-out 0.3s;
        }
    }

    .highlight {
        background-color: var(--color-warning-bg);
        font-weight: var(--font-weight-bold) !important;
        transition: all ease-in-out 0.3s;
    }

    &:hover {
        background: var(--stone-50);

        .highlighter__ticker {
            background: var(--stone-200);

            .highlight {
                color: var(--stone-800);
            }

            span {
                color: var(--stone-800);
            }
        }
    }

    .country-chip {
        width: 20px;
        height: 20px;
        border-radius: 50%;
        display: inline-block;
        overflow: hidden;
        position: relative;
        top: 2px;
    }
}
</style>
